import { DispatcherEvent } from '@carvana/merch-native';
import { useContext, useMemo } from 'react';
import { setCorrelationContext } from '~/clients';
import UserContext from '~/context/common/UserContext';
import type { StepConfiguration } from '~/contracts/common';
import {
  useClosedAccountModal,
  useOpenAccountModal
} from '~/hooks/common/accountModal/useAccountModal';
import { fetchUserProfile } from '~/services/common';
import { logError } from '~/utilities/loggers';
import type { AddFavoritesFlow, FavoritesArguments } from './types';

const defaultModalSettings: StepConfiguration = {
  displayHeading: 'SAVE TO FAVORITES',
  displayTitle: 'SAVE TO FAVORITES'
};

async function checkHasUserProfile(
  controller: AbortController = new AbortController()
) {
  try {
    const { customerId: userId } = await fetchUserProfile(controller);

    if (userId) {
      setCorrelationContext(userId);
    }

    return Boolean(userId);
  } catch (err) {
    logError(err, { location: 'Check Has User Profile' });
  }
}

export default function useLogInAndFavorite(): AddFavoritesFlow {
  const {
    userState: {
      data: { isLoggedIn }
    }
  } = useContext(UserContext);
  const openAccountModal = useOpenAccountModal();
  const closedAccountModal = useClosedAccountModal();

  return useMemo(
    () =>
      (onContinue: (args: FavoritesArguments) => void) =>
      async (args: FavoritesArguments) => {
        if (isLoggedIn) {
          onContinue(args);

          return;
        }

        const { modalSettings = defaultModalSettings } = args;

        openAccountModal({
          ...modalSettings,
          callerIdentifier: 'Favorites',
          nativeOptions: {
            eventPayload: {
              stockNumber: args.stockNumber,
              subscribeToNotifications: args.subscribeToNotifications,
              vehicleId: args.vehicleId
            },
            eventDispatch: DispatcherEvent.AddFavorite
          },
          onSuccess: async () => {
            const hasUserProfile = await checkHasUserProfile();

            closedAccountModal('submit');

            if (hasUserProfile) {
              onContinue(args);
            }
          }
        });
      },
    [closedAccountModal, isLoggedIn, openAccountModal]
  );
}
